import { useState } from 'react';
import { MessageCircle, Send, X, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';

const MessagePassenger = ({ booking, onClose }) => {
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState(() => {
    // Load messages from localStorage
    const saved = localStorage.getItem(`messages_${booking.id}`);
    return saved ? JSON.parse(saved) : [];
  });

  const quickMessages = [
    "I'm on my way to pick you up!",
    "I'll be there in 5 minutes.",
    "I've arrived at the pickup location.",
    "Running a few minutes late, sorry for the delay.",
    "I'm in a [vehicle color/type]. Look for me!",
    "Please meet me at [specific location].",
    "Traffic is heavy, ETA 10 minutes.",
    "I'm waiting at the pickup point."
  ];

  const sendMessage = (messageText) => {
    const newMessage = {
      id: Date.now(),
      text: messageText,
      timestamp: new Date().toISOString(),
      sender: 'driver'
    };

    const updatedMessages = [...messages, newMessage];
    setMessages(updatedMessages);
    
    // Save to localStorage
    localStorage.setItem(`messages_${booking.id}`, JSON.stringify(updatedMessages));
    
    // In production, this would send to backend/SMS
    toast.success('Message sent to passenger');
    
    // Send SMS (mock - in production would call backend API)
    const smsText = encodeURIComponent(messageText);
    console.log(`SMS would be sent to ${booking.phone}: ${messageText}`);
    
    setMessage('');
  };

  const handleSendCustomMessage = () => {
    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }
    sendMessage(message);
  };

  const handleQuickMessage = (text) => {
    sendMessage(text);
  };

  const callPassenger = () => {
    window.location.href = `tel:${booking.phone}`;
  };

  const sendSMS = () => {
    const smsBody = encodeURIComponent("Hi, this is your RELAX & RIDE driver. ");
    window.location.href = `sms:${booking.phone}?body=${smsBody}`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] flex flex-col">
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MessageCircle className="w-6 h-6 text-red-600" />
              <div>
                <CardTitle>Message Passenger</CardTitle>
                <p className="text-sm text-gray-600 mt-1">
                  {booking.name} • {booking.phone}
                </p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="flex-1 overflow-y-auto p-6">
          {/* Quick Action Buttons */}
          <div className="flex gap-3 mb-6">
            <Button 
              onClick={callPassenger}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              <Phone className="w-4 h-4 mr-2" />
              Call Now
            </Button>
            <Button 
              onClick={sendSMS}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Open SMS
            </Button>
          </div>

          {/* Message History */}
          {messages.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-600 mb-3">Message History</h3>
              <div className="space-y-2">
                {messages.map((msg) => (
                  <div key={msg.id} className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm">{msg.text}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(msg.timestamp).toLocaleTimeString()}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick Messages */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-600 mb-3">Quick Messages</h3>
            <div className="grid grid-cols-1 gap-2">
              {quickMessages.map((text, idx) => (
                <button
                  key={idx}
                  onClick={() => handleQuickMessage(text)}
                  className="text-left p-3 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors text-sm"
                >
                  {text}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Message */}
          <div>
            <h3 className="text-sm font-semibold text-gray-600 mb-3">Custom Message</h3>
            <div className="flex gap-2">
              <Textarea
                placeholder="Type your message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                className="flex-1"
              />
              <Button 
                onClick={handleSendCustomMessage}
                className="bg-red-600 hover:bg-red-700 self-end"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <p className="text-xs text-gray-500 mt-4 text-center">
            Messages are sent via SMS to the passenger's phone
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default MessagePassenger;
